import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Loader } from 'rsuite';
import {
  markAllAsRead,
  fetchNotifications,
  fetchOldNotifications,
} from '@app/store/slices/notificationsSlice';
import { icons } from '@shared/static/icons';
import TabsComponent from '@shared/ui/TabsComponent';
import EmptyData from '@shared/ui/messages/EmptyData';
import { formatMonthTime } from '@utils/date-helpers';

const PAGE_SIZE = 20;

export function NotificationsActions() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.notifications);
  return (
    <div className="notifications__actions">
      {isLoading ? (
        <Loader />
      ) : (
        <button
          className="dashed-btn"
          type="button"
          onClick={() => dispatch(markAllAsRead())}
        >
          {icons.check} {t('mark_all_as_read')}
        </button>
      )}
    </div>
  );
}

export default function NotificationsContent() {
  const { t } = useTranslation();
  const [start, setStart] = useState(1);
  const dispatch = useDispatch();
  const [tab, setTab] = useState('new');

  const {
    notifications_new,
    count_new,
    notifications_old,
    count_old,
    count_unread,
    isListLoading,
    error,
  } = useSelector((state) => state.notifications);

  const isNew = tab === 'new';
  const fetchList = isNew ? fetchNotifications : fetchOldNotifications;
  const data = isNew ? notifications_new : notifications_old;
  const total = isNew ? count_new : count_old;

  useEffect(() => {
    setStart(1);
    dispatch(fetchList({ count: PAGE_SIZE, start: 1 }));
  }, [dispatch, fetchList]);

  function fetchNext() {
    const nextStart = start + PAGE_SIZE;
    setStart(nextStart);
    dispatch(fetchList({ count: PAGE_SIZE, start: nextStart }));
  }

  return (
    <>
      <div className="block-pd-x">
        <TabsComponent
          options={[
            {
              label: t('New'),
              value: 'new',
              icon: (
                <div
                  className={`custom-badge ${count_unread === 0 ? 'no-value' : ''}`}
                >
                  {count_unread}
                </div>
              ),
            },
            { label: t('Archive'), value: 'archive', icon: 'archive' },
          ]}
          value={tab}
          setValue={setTab}
          fullWidth
          isGroup
        />
      </div>
      <div
        id="notifications-scroll-container"
        className="block-pd-x drawerFilter-grow"
      >
        <div className={`notifications ${total === 0 ? 'empty' : ''}`}>
          <NotificationsList
            data={data}
            fetchNext={fetchNext}
            hasMore={data.length < total}
            isLoading={isListLoading}
            error={error}
          />
        </div>
      </div>
    </>
  );
}

function NotificationsList({ data, fetchNext, hasMore, isLoading, error }) {
  const { t } = useTranslation();

  if (data.length === 0) {
    return (
      <div className="notifications__list">
        {isLoading ? (
          <Loader center />
        ) : (
          <EmptyData text={error ? t('data_fetch_error') : t('nothing_yet')} />
        )}
      </div>
    );
  }

  return (
    <div className="notifications__list">
      <InfiniteScroll
        dataLength={data.length}
        next={fetchNext}
        hasMore={hasMore}
        scrollableTarget="notifications-scroll-container"
        scrollThreshold={0.9}
      >
        {data.map((item) => (
          <div key={item.id} className="notifications__item">
            <div className="notifications__item-header">
              <p className="fw-500">{item.title}</p>
              <p className="secondary-text">
                {formatMonthTime(item.created_at)}
              </p>
            </div>
            <div
              className="notifications__item-body"
              dangerouslySetInnerHTML={{ __html: item.body }}
            />
          </div>
        ))}
      </InfiniteScroll>
    </div>
  );
}
